import React from "react";
import { FaCheckCircle, FaClock, FaExclamationTriangle, FaFileSignature, FaLock, FaPaperPlane } from "react-icons/fa";
import StatusBadge from "../StatusBadge";
import { colors, spacing, typography, radius, transitions } from "../../utils/designTokens";
import {
  estadoActa,
  plazoComunicacion,
  diasHastaPublicacion,
  fechaCorta,
  fechaHora,
  totalMarcas,
  marcasPendientes,
  PLAZO_COMUNICACION_DIAS,
} from "./actaFlujo";

// Banner de estado del acta en el Editor: estado y versión, la cuenta de los
// 30 días para comunicarla a los copropietarios y, ya publicada, el número,
// la fecha y el resultado de los avisos. Es solo presentación.
//
// Props:
//   meta     { status, version, origen, actaNumero, publicadaEnMs, envioActa, updatedAtMs }
//   acta     data del acta (para la fecha de la asamblea)
//   revision { marcas } de la IA
//   dirty    hay cambios sin guardar
//   ahoraMs  reloj inyectable (tests)
//   onVerRevision () => void  abre el panel de revisión
//   compacto bool  versión angosta para la columna del Editor

const TONOS = {
  normal: { bg: colors.primarySubtle, text: colors.gray900, acento: colors.primary },
  alerta: { bg: colors.warningLight, text: colors.warningDark, acento: colors.warningDark },
  vencido: { bg: colors.errorLight, text: colors.errorDark, acento: colors.errorDark },
  publicada: { bg: colors.successLight, text: colors.successDark, acento: colors.successDark },
};

function textoPlazo(plazo) {
  if (!plazo) return `Cargá la fecha de la asamblea para ver el plazo de ${PLAZO_COMUNICACION_DIAS} días para comunicar el acta.`;
  if (plazo.vencido) {
    return `El plazo de ${PLAZO_COMUNICACION_DIAS} días para comunicar el acta venció el ${fechaCorta(plazo.vence)}: publicala cuanto antes.`;
  }
  if (plazo.diasRestantes === 0) return "Hoy vence el plazo para comunicar el acta a los propietarios.";
  return `${plazo.diasRestantes === 1 ? "Queda 1 día" : `Quedan ${plazo.diasRestantes} días`} para comunicar el acta a los propietarios (vence el ${fechaCorta(plazo.vence)}).`;
}

export default function ActaEstadoBanner({ meta, acta, revision, dirty = false, ahoraMs, onVerRevision, compacto = false }) {
  if (!meta) return null;
  const est = estadoActa(meta.status);
  const publicada = meta.status === "publicada";
  const plazo = publicada ? null : plazoComunicacion(acta && acta.fecha, ahoraMs ?? Date.now());
  const marcas = publicada ? 0 : totalMarcas(revision);
  const pendientes = publicada ? 0 : marcasPendientes(revision);

  let tono = TONOS.normal;
  if (publicada) tono = TONOS.publicada;
  else if (plazo && plazo.vencido) tono = TONOS.vencido;
  else if (plazo && plazo.diasRestantes <= 5) tono = TONOS.alerta;

  const partes = [];
  if (publicada) {
    partes.push(meta.actaNumero ? `Acta N° ${meta.actaNumero}` : "Acta publicada");
    if (meta.publicadaEnMs) partes.push(`publicada el ${fechaHora(meta.publicadaEnMs)}`);
  } else if (meta.id) {
    partes.push(`Versión ${meta.version || 0}`);
    if (meta.updatedAtMs) partes.push(`guardada el ${fechaHora(meta.updatedAtMs)}`);
  } else {
    partes.push("Todavía no se guardó");
  }
  partes.push(meta.origen === "ia" ? "redactada por la IA" : "redactada a mano");

  const diasPub = publicada ? diasHastaPublicacion(acta && acta.fecha, meta.publicadaEnMs) : null;
  const envio = publicada ? meta.envioActa : null;

  return (
    <div style={{ ...S.banner, ...(compacto ? S.compacto : {}), background: tono.bg, color: tono.text }} role="status" aria-live="polite">
      <div style={S.fila}>
        <StatusBadge status={est.badge} label={est.label} size="sm" />
        <span style={S.meta}>{partes.join(" · ")}</span>
        {dirty && !publicada && <span style={{ ...S.meta, color: colors.warningDark }}>· Cambios sin guardar</span>}
      </div>

      {publicada ? (
        <>
          <div style={S.fila}>
            <FaPaperPlane size={12} color={tono.acento} aria-hidden="true" />
            <span>
              {diasPub != null
                ? `Comunicada a los ${diasPub} ${diasPub === 1 ? "día" : "días"} de la asamblea${diasPub <= PLAZO_COMUNICACION_DIAS ? ", dentro del plazo" : ", fuera del plazo de 30 días"}.`
                : "Comunicada a los propietarios."}
              {envio && Number(envio.total) > 0 && (
                <>
                  {" "}Avisos: {Number(envio.enviados) || 0} de {Number(envio.total)} enviados
                  {Number(envio.fallidos) > 0 ? ` · ${envio.fallidos} con error` : ""}.
                </>
              )}
            </span>
          </div>
          <div style={S.fila}>
            <FaLock size={12} color={tono.acento} aria-hidden="true" />
            <span>El acta publicada queda bloqueada: solo lectura y descarga del PDF.</span>
          </div>
          <div style={S.fila}>
            <FaFileSignature size={12} color={meta.firmada ? tono.acento : colors.warningDark} aria-hidden="true" />
            <span>
              {meta.firmada
                ? `Acta firmada${meta.firmada.version > 1 ? ` (versión ${meta.firmada.version})` : ""} subida${meta.firmada.subidaEnMs ? ` el ${fechaHora(meta.firmada.subidaEnMs)}` : ""}${meta.firmada.subidaPor ? ` por ${meta.firmada.subidaPor}` : ""}: es la que ven los propietarios.`
                : "Falta subir el acta firmada: hasta entonces, los propietarios ven la que generó Habitia."}
            </span>
          </div>
        </>
      ) : (
        <>
          <div style={S.fila}>
            <FaClock size={12} color={tono.acento} aria-hidden="true" />
            <span>{textoPlazo(plazo)}</span>
          </div>
          {marcas > 0 && (
            <div style={S.fila}>
              {pendientes > 0 ? (
                <FaExclamationTriangle size={12} color={colors.warningDark} aria-hidden="true" />
              ) : (
                <FaCheckCircle size={12} color={colors.success} aria-hidden="true" />
              )}
              <span>
                {pendientes === marcas
                  ? `La IA dejó ${marcas} ${marcas === 1 ? "marca" : "marcas"} para revisar antes de publicar.`
                  : pendientes > 0
                    ? `${pendientes === 1 ? "Queda 1" : `Quedan ${pendientes}`} de ${marcas} marcas de la IA por revisar antes de publicar.`
                    : marcas === 1
                      ? "Revisaste la marca de la IA."
                      : `Revisaste las ${marcas} marcas de la IA.`}
              </span>
              {onVerRevision && (
                <button type="button" style={S.link} onClick={onVerRevision}>
                  Ver revisión
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const S = {
  banner: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
    padding: `${spacing[3]}px ${spacing[5]}px`,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    lineHeight: 1.5,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  compacto: { padding: `${spacing[2]}px ${spacing[4]}px`, fontSize: typography.sizes.sm, lineHeight: 1.45 },
  fila: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  meta: { fontSize: "inherit" },
  link: {
    background: "transparent",
    border: 0,
    padding: 0,
    color: colors.primaryDark,
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    fontSize: "inherit",
    fontWeight: 400,
    borderRadius: radius.sm,
    transition: `color ${transitions.fast}`,
  },
};
